import { DefaultDisabledBenefitValues } from "@platform/openapi-types";

import { BenefitDefinition } from "../../../benefits/benefit.types";
import { CellSpec } from "../../shared/cellSpecs";

import {
  applyCellEnumValues,
  createTypeDefinition,
  createValueDefinition,
} from "./builderUtils";

export interface TypeAmountBenefitPairConfig {
  prefix: string;
  typeLabel: string;
  typeCell: CellSpec;
  typeEnumValues?: string[];
  typeCustomLabels?: Record<string, string>;
  amountLabel?: string;
  amountCell: CellSpec;
  typeDescription: string;
  amountDescription: string;
  disabledValues?: string[];
  nested?: boolean;
  networkDependent?: boolean;
  typeDefaultValue?: unknown;
  amountDefaultValue?: unknown;
  valueSuffix?: string;
  amountCellVariants?: {
    dependsOn?: string;
    options: Record<string, CellSpec>;
    default: CellSpec;
  };
  typeEditableWhen?: BenefitDefinition["editableWhen"];
  isRequired?: boolean;
  isValueRequired?: boolean;
  revertAmountOnInvalid?: boolean;
  /**
   * The MP configuration field that shows or hides this row. Set on the type
   * definition, and on the amount field too when it is not nested — a nested
   * field renders with its parent and goes when the parent goes, but a
   * top-level one has to carry the key itself.
   */
  mpConfigKey?: string;
  mpOnly?: boolean;
}

/**
 * Builds a type/amount benefit definition pair.
 *
 * Creates two related benefit definitions:
 * - `{prefix}Type`: The type selection field
 * - `{prefix}Amount` (or custom suffix): The amount value field (conditionally editable)
 */
export function buildTypeAmountBenefitPair<
  TPrefix extends string,
  TSuffix extends string = "Amount",
>(
  config: TypeAmountBenefitPairConfig & {
    prefix: TPrefix;
    valueSuffix?: TSuffix;
  },
): {
  [K in `${TPrefix}Type` | `${TPrefix}${TSuffix}`]: BenefitDefinition;
} {
  const {
    prefix,
    typeLabel,
    typeCell,
    typeEnumValues,
    typeCustomLabels,
    amountLabel = "Amount",
    amountCell,
    typeDescription,
    amountDescription,
    disabledValues,
    nested = true,
    networkDependent = false,
    typeDefaultValue,
    amountDefaultValue,
    valueSuffix = "Amount",
    amountCellVariants,
    typeEditableWhen,
    isRequired,
    isValueRequired,
    revertAmountOnInvalid,
    mpConfigKey,
    mpOnly,
  } = config;

  const finalDisabledValues = disabledValues ?? [
    ...DefaultDisabledBenefitValues,
  ];

  const finalTypeCell = applyCellEnumValues(
    typeCell,
    typeEnumValues,
    typeDefaultValue,
    typeCustomLabels,
  );

  const typeDef = createTypeDefinition(
    typeLabel,
    typeDescription,
    finalTypeCell,
    networkDependent,
    prefix,
    valueSuffix,
    typeDefaultValue,
    isRequired,
  );
  if (typeEditableWhen) {
    typeDef.editableWhen = typeEditableWhen;
  }
  if (mpConfigKey) {
    typeDef.mpConfigKey = mpConfigKey;
  }

  const typeDefLabel = `${prefix}Type`;
  const cellVariants = amountCellVariants
    ? {
        dependsOn: amountCellVariants.dependsOn ?? typeDefLabel,
        ...amountCellVariants,
      }
    : undefined;

  const amountDef = createValueDefinition(
    amountLabel,
    amountDescription,
    amountCell,
    networkDependent,
    nested,
    prefix,
    finalDisabledValues,
    amountDefaultValue,
    cellVariants,
    isValueRequired,
  );

  if (revertAmountOnInvalid) {
    amountDef.revertOnInvalid = true;
  }
  // A nested amount renders with its type row and disappears with it; a
  // top-level one is its own row, so it needs the key to be hidden too.
  if (mpConfigKey && !nested) {
    amountDef.mpConfigKey = mpConfigKey;
  }

  if (mpOnly !== undefined) {
    typeDef.mpOnly = mpOnly;
    amountDef.mpOnly = mpOnly;
  }

  return {
    [typeDefLabel]: typeDef,
    [`${prefix}${valueSuffix}`]: amountDef,
  } as {
    [K in `${TPrefix}Type` | `${TPrefix}${TSuffix}`]: BenefitDefinition;
  };
}

export interface TypeAmountWithVariantsBenefitConfig {
  prefix: string;
  typeLabel: string;
  typeCell: CellSpec;
  typeEnumValues: string[];
  amountLabel?: string;
  typeDescription: string;
  amountDescription: string;
  disabledValues?: string[];
  nested?: boolean;
  networkDependent?: boolean;
  typeDefaultValue?: unknown;
  cellSpecVariants: Record<string, CellSpec>;
  defaultCellSpec: CellSpec;
  isRequired?: boolean;
}

/**
 * Builds a type/amount benefit pair where the amount cell spec changes based on
 * the type field's value (e.g., percentage vs dollar amount).
 */
export function buildTypeAmountWithVariantsBenefit<TPrefix extends string>(
  config: TypeAmountWithVariantsBenefitConfig & { prefix: TPrefix },
): {
  [K in `${TPrefix}Type` | `${TPrefix}Amount`]: BenefitDefinition;
} {
  const {
    prefix,
    typeLabel,
    typeCell,
    typeEnumValues,
    amountLabel = "Amount",
    typeDescription,
    amountDescription,
    disabledValues,
    nested = true,
    networkDependent = false,
    typeDefaultValue,
    cellSpecVariants,
    defaultCellSpec,
    isRequired,
  } = config;

  const finalDisabledValues = disabledValues ?? [
    ...DefaultDisabledBenefitValues,
  ];

  const finalTypeCell = applyCellEnumValues(
    typeCell,
    typeEnumValues,
    typeDefaultValue,
  );

  const typeDef = createTypeDefinition(
    typeLabel,
    typeDescription,
    finalTypeCell,
    networkDependent,
    prefix,
    "Amount",
    typeDefaultValue,
    isRequired,
  );

  const amountDef: BenefitDefinition = {
    label: amountLabel,
    description: amountDescription,
    cell: defaultCellSpec,
    networkDependent,
    nested,
    editableWhen: {
      dependsOn: `${prefix}Type`,
      disabledValues: finalDisabledValues,
    },
    cellVariants: {
      dependsOn: `${prefix}Type`,
      options: cellSpecVariants,
      default: defaultCellSpec,
    },
  };

  return {
    [`${prefix}Type`]: typeDef,
    [`${prefix}Amount`]: amountDef,
  } as {
    [K in `${TPrefix}Type` | `${TPrefix}Amount`]: BenefitDefinition;
  };
}

/**
 * Numeric guards + the conditional type/amount builder — moved to
 * `@platform/btx-benefit-transforms` so the per-LOC transforms can reuse them
 * outside the React grid. Re-exported here to keep the local import path stable.
 */
export {
  isValidAmount,
  isValidCount,
  buildConditionalTypeAmount,
} from "@platform/btx-benefit-transforms";
